/**
 * REAL 13-Week Operator Strength Program
 * Based on actual program_data.json structure
 * 
 * Structure:
 * - Weeks 1 & 13: Testing weeks
 * - Weeks 2-6: Accumulation Block
 * - Week 7: Deload
 * - Weeks 8-12: Intensification Block
 */

import type { WorkoutProgram, WorkoutTemplate, WorkoutTemplateDay, ProgramMetadata } from '../types/program';
import { OPERATOR_PERIODIZATION } from '../operatorPeriodization';

const metadata: ProgramMetadata = {
  id: 'operator-strength-13week',
  name: '13-Week Operator Strength',
  description: 'Elite tactical athlete program combining strength, endurance, and work capacity',
  level: 'operator',
  duration: 13,
  daysPerWeek: 6,
  goals: ['tactical-performance', 'strength', 'endurance', 'work-capacity'],
  difficulty: 'advanced',
  version: '2.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false, // Day 6 is a standardized Work Capacity training day (not a range/enhancement day)
  assessmentFormat: 'sf-fitness-test', // Redirect to /assessment/{day} for SF-style fitness testing
  // SF-style strength program with built-in GPP cardio days. 120 min/wk
  // Z2 maintains aerobic base without competing with strength adaptation.
  weeklyZ2Target: 120,
};

// ============================================================================
// TESTING WEEK TEMPLATES (Weeks 1 & 13)
// ============================================================================

// SF/Ranger-standard assessment battery. Kept IDENTICAL to Special Forces
// Sustainment's testing week (specialForcesSustainment6Week.ts) — both
// programs run the same standard so scores are comparable across them:
//   Day 1  Deadlift 3RM · Bench bodyweight max reps · Plank · HR Push-Ups · 1.5mi run
//   Day 3  6-mile ruck (35 lb)
//   Day 5  Tactical AMRAP
//   Day 6  Back Squat / Bench / Deadlift 1RM
const testingWeekDay1: WorkoutTemplateDay = {
  name: 'Operator Test Day 1',
  sessionType: 'assessment',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Jog or bike — build a light sweat before testing' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Dynamic hips before the run' },
        { name: 'World\'s Greatest Stretch', sets: 1, reps: '5/side', format: 'prep', details: 'Open hips + t-spine' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation before the deadlift' },
        { name: 'Warm-Up Ramp Sets', format: 'prep', details: 'Ramp the deadlift + bench to a heavy prep set before the max effort. Do not test cold.' },
      ],
    },
    {
      name: 'Strength Test',
      type: 'testing',
      exercises: [
        { name: 'Deadlift', sets: 1, reps: 3, intensity: 'Max', rest: '3-5 min', details: 'Heaviest triple, no bounce', sessionType: 'assessment' },
        { name: 'Bench Press', sets: 1, reps: 'Max', intensity: 'Bodyweight', rest: '3-5 min', details: 'Strict ROM, max reps at bodyweight', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Work Capacity Testing',
      type: 'testing',
      exercises: [
        { name: 'Plank', sets: 1, reps: 'Max Time', format: 'bodyweight', details: 'Hold for max time', sessionType: 'assessment' },
        { name: 'Hand Release Push-Ups', sets: 1, reps: 'Max', format: 'bodyweight', details: 'Chest to ground, hands off', sessionType: 'assessment' },
        { name: '1.5 Mile Run', duration: 'For Time', format: 'distance-time', details: 'Record time', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'After the run' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const testingWeekDay3: WorkoutTemplateDay = {
  name: '6 Mile Ruck Test',
  sessionType: 'assessment',
  estimatedDuration: 120,
  blocks: [
    {
      name: 'Ruck Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Brisk unloaded walk — warm the legs before loading' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for a loaded gait' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before the march' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Carry the ruck with the posterior chain, not the low back' },
      ],
    },
    {
      name: 'Ruck Test',
      type: 'testing',
      exercises: [
        { name: '6 Mile Ruck', duration: 'For Time', format: 'ruck', details: '35 lb (before water)', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee — the ruck hammers calves' },
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after loaded walking' },
        { name: 'Lower Back Stretch', duration: '1 min', format: 'recovery', details: 'Child\'s pose / cat-cow — decompress the spine' },
      ],
    },
  ],
};

const testingWeekDay5: WorkoutTemplateDay = {
  name: 'Tactical AMRAP',
  sessionType: 'assessment',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Row or jog — raise core temp' },
        { name: 'World\'s Greatest Stretch', sets: 1, reps: '5/side', format: 'prep', details: 'Open hips + t-spine' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap + rear delt before the pull-ups' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Prime the shoulders for the push-ups' },
      ],
    },
    {
      name: 'Work Capacity AMRAP',
      type: 'testing',
      exercises: [
        { name: 'Tactical AMRAP', duration: '10 min', format: 'circuit', details: '5 Pull-Ups, 10 Push-Ups, 15 Squats, 200m Run (0.12 miles on treadmill) - AMRAP. Plate carrier optional. Passing: 4+ rounds', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the push-ups' },
        { name: 'Quad Stretch', duration: '45s/side', format: 'recovery', details: 'After the squats + run' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const testingWeekDay6: WorkoutTemplateDay = {
  name: '1RM Max Testing',
  sessionType: 'assessment',
  estimatedDuration: 90,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for squat depth' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep', details: 'Groove the hinge before the deadlift' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before the bench max' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30s', format: 'prep', details: 'Prime quads + patellar tendon — knee-friendly' },
        { name: 'Warm-Up Ramp Sets', format: 'prep', details: 'Per lift: empty bar → 40% → 60% → 75% → 85% → 90%+ singles before your first max attempt. Do not test cold.' },
      ],
    },
    {
      name: '1RM Testing',
      type: 'testing',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: 1, intensity: 'Max', rest: '5 min', details: '1RM attempt', sessionType: 'assessment' },
        { name: 'Bench Press', sets: 1, reps: 1, intensity: 'Max', rest: '5 min', details: '1RM attempt', sessionType: 'assessment' },
        { name: 'Deadlift', sets: 1, reps: 1, intensity: 'Max', rest: '5 min', details: '1RM attempt', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after squatting' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress the spine after the deadlift' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

// ============================================================================
// TRAINING CYCLE TEMPLATES (Weeks 2-13)
// ============================================================================

const trainingDay1: WorkoutTemplateDay = {
  name: 'Gym Strength (Lower + Push/Pull)',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before hinging' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Wake the posterior chain for the deadlift' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep', details: 'Groove the hinge before loading' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before the incline press' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap + rear delt before the row' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Deadlift', sets: 4, reps: 5, intensity: 'progressive', details: 'Conventional or trap bar; controlled tempo' },
        { name: 'Front Box Jump', sets: 4, reps: 5, intensity: 'explosive', details: 'Single max-height effort, stick the landing, step down', format: 'plyometric' },
      ],
    },
    {
      name: 'Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Incline Dumbbell Bench Press', sets: 4, reps: 5, intensity: 'progressive', details: '2-sec eccentric, full lockout' },
        { name: 'Barbell Row (Pendlay)', sets: 4, reps: 5, intensity: 'progressive', details: 'From floor, strict torso' },
      ],
    },
    {
      name: 'Superset C',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Bulgarian Split Squat', sets: 3, reps: 10, intensity: 'progressive', details: 'Rear foot elevated, per leg' },
        { name: 'Hanging Leg Raise', sets: 3, reps: 10, intensity: 'progressive', details: 'Controlled, no swing' },
      ],
    },
    {
      name: 'Superset D',
      type: 'finisher',
      format: 'superset',
      exercises: [
        { name: 'Kettlebell Swing', sets: 3, reps: 15, intensity: 'progressive', details: 'Hip hinge, explosive' },
        { name: 'Farmer Carry', sets: 3, reps: '40m', intensity: 'progressive', details: 'Heavy, controlled' },
      ],
    },
    {
      name: 'Superset E',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Preacher Curl', sets: 3, reps: 10, intensity: 'progressive', details: 'Strict form' },
        { name: 'Tricep Pulldown', sets: 3, reps: 10, intensity: 'progressive', details: 'Full extension' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after the split squats' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress the spine + flush the grip after pulls + carries' },
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after pressing' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const trainingDay2: WorkoutTemplateDay = {
  name: 'Swim Day',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Dry-land scap + rear delt before you get in' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime — freestyle is shoulder-heavy' },
        { name: 'Technical Drills', duration: '10 min', format: 'prep', details: 'In water: catch-up, fingertip drag, build to pace' },
      ],
    },
    {
      name: 'Main Swim',
      type: 'conditioning',
      exercises: [
        { name: '50M Sprints', sets: 8, reps: 1, intensity: 'progressive', rest: '30-45s', format: 'intervals', details: 'Max effort, track times' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Easy Swim', duration: '5-10 min', format: 'recovery', details: 'Easy laps to flush' },
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the pulling volume' },
      ],
    },
  ],
};

const trainingDay3: WorkoutTemplateDay = {
  name: 'Arms and Accessory Day',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Jog or bike — build a light sweat' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Front-back + lateral — dynamic hips for sprinting' },
        { name: 'Glute Bridge', sets: 1, reps: '15', format: 'prep', details: 'Glute activation before sprinting' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Warm the scap + rear delts before the arm + shoulder work' },
        { name: 'Strides', sets: 4, reps: '40m', format: 'prep', details: 'Build 60% → 80% → 90%. Prepping the tissue before all-out is how you avoid a torn hamstring.' },
      ],
    },
    {
      name: 'Sprints',
      type: 'conditioning',
      exercises: [
        { name: '30/60s Sprints', sets: 6, reps: 1, intensity: 'max', rest: '2-3 min', format: 'intervals', details: '30s sprint, 60s walk' },
      ],
    },
    {
      name: 'Arms Superset A',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Barbell Curl', sets: 4, reps: 10, intensity: 'progressive', rest: '60s', details: 'Strict form, no swing' },
        { name: 'Skull Crusher', sets: 4, reps: 10, intensity: 'progressive', rest: '60s', details: 'Control eccentric' },
      ],
    },
    {
      name: 'Arms Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Cable Curl', sets: 3, reps: 10, intensity: 'progressive', rest: '45s', details: 'Constant tension' },
        { name: 'Rope Tricep Pushdown', sets: 3, reps: 10, intensity: 'progressive', rest: '45s', details: 'Full extension' },
      ],
    },
    {
      name: 'Shoulder Complex',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Lateral Raise', sets: 3, reps: 15, intensity: 'RPE 7', rest: '30s', details: 'Slight forward lean, control movement' },
        { name: 'Rear Delt Fly', sets: 3, reps: 15, intensity: 'RPE 7', rest: '30s', details: 'Chest supported or bent over' },
        { name: 'Face Pull', sets: 3, reps: 15, intensity: 'RPE 7', rest: '30s', details: 'Pull to face level, external rotation' },
        { name: 'Shrug', sets: 3, reps: 15, intensity: 'RPE 7', rest: '30s', details: 'Straight up and down, no rolling' },
      ],
    },
    {
      name: 'Core Circuit',
      type: 'circuit',
      format: 'circuit',
      exercises: [
        { name: 'Hanging Leg Raise', sets: 3, reps: 12, format: 'circuit', rest: '30s' },
        { name: 'Ab Wheel', sets: 3, reps: 10, format: 'circuit', rest: '30s' },
        { name: 'Russian Twist', sets: 3, reps: 20, format: 'circuit', rest: '30s', details: '20 total (10 each side)' },
        { name: 'Plank', sets: 3, reps: '30s', format: 'circuit', rest: '30s' },
        { name: 'Side Plank', sets: 3, reps: '30s each', format: 'circuit', rest: '30s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'Hamstrings take the brunt of the sprints' },
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the pressing volume' },
        { name: 'Cross-Body Shoulder Stretch', duration: '30s/side', format: 'recovery', details: 'Rear delt after the shoulder complex' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const trainingDay4: WorkoutTemplateDay = {
  name: 'Endurance Ruck',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Ruck Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Brisk unloaded walk — warm the legs before loading' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for a loaded gait' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before the march' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Carry the ruck with the posterior chain, not the low back' },
      ],
    },
    {
      name: 'Main Ruck',
      type: 'conditioning',
      exercises: [
        { name: 'Ruck March', duration: '3 miles', format: 'ruck', intensity: 'progressive', details: '35 lb (before water), steady pace' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee — the ruck hammers calves' },
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after loaded walking' },
        { name: 'Lower Back Stretch', duration: '1 min', format: 'recovery', details: 'Child\'s pose / cat-cow — decompress the spine' },
      ],
    },
  ],
};

// Additional training days (Day 5, 6, 7) - simplified for now
const trainingDay5: WorkoutTemplateDay = {
  name: 'Gym Strength (Hybrid)',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for front-squat depth' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30s', format: 'prep', details: 'Primes quads + patellar tendon — knee-friendly' },
        { name: 'T-Spine Extension', sets: 1, reps: '8', format: 'prep', details: 'Thoracic mobility for the front rack + overhead' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before the overhead press' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap for the pull-ups + rows' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Front Squat', sets: 4, reps: 5, intensity: 'progressive', details: 'Elbows high, depth' },
        { name: 'Broad Jump', sets: 4, reps: 5, intensity: 'explosive', format: 'plyometric', details: 'Explosive' },
      ],
    },
    {
      name: 'Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Overhead Press', sets: 4, reps: 5, intensity: 'progressive', details: 'Strict, no lean' },
        { name: 'Weighted Pull-Up', sets: 4, reps: 5, intensity: 'progressive', details: 'Add weight as needed' },
      ],
    },
    {
      name: 'Superset C',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 4, reps: 8, intensity: 'progressive', details: 'Neutral grip ok' },
        { name: 'Chest Supported Row', sets: 4, reps: 8, intensity: 'progressive', details: 'Pause at chest' },
      ],
    },
    {
      name: 'Superset D',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Walking Lunge', sets: 3, reps: 10, intensity: 'progressive', rest: '60-75s', details: 'Long stride, per leg' },
        { name: 'Suitcase Carry', sets: 4, reps: '30-40m', intensity: 'progressive', rest: 'Walk back', details: 'Anti-lateral flexion, per side' },
      ],
    },
    {
      name: 'Finisher',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 3, reps: '12 each', intensity: 'RPE 7', rest: '60s', details: 'Slow, strict' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Quads + hip flexors after front squats + lunges' },
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after pressing' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress + flush the lats after pull-ups + carries' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

// Standardized work-capacity day. Fills the GPP / engine + loaded-carry
// gap the two gym-strength days (D1, D5) don't cover: a power press +
// pull, then a tactical grinder circuit and hard core.
const trainingDay6: WorkoutTemplateDay = {
  name: 'Work Capacity',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
        { name: 'World\'s Greatest Stretch', sets: 1, reps: '5/side', format: 'prep', details: 'Full-body dynamic opener' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation for the swings + carries' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap + rear delt before pressing overhead' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Push Press', sets: 4, reps: 6, intensity: 'progressive', details: 'Drive with the legs, lock out overhead' },
        { name: 'Pull-Up', sets: 4, reps: 'AMRAP', intensity: 'bodyweight', details: 'Max clean reps, full hang to chin over bar' },
      ],
    },
    {
      name: 'Conditioning Circuit',
      type: 'conditioning',
      format: 'circuit',
      exercises: [
        { name: 'Kettlebell Swing', sets: 4, reps: 20, intensity: 'progressive', format: 'circuit', details: 'Hip hinge, explosive' },
        { name: 'Sled Push', sets: 4, reps: '40m', intensity: 'progressive', format: 'circuit', details: 'Low, driving steps' },
        { name: 'Farmer Carry', sets: 4, reps: '40m', intensity: 'progressive', format: 'circuit', details: 'Heavy, tall posture' },
        { name: 'Burpee', sets: 4, reps: 12, format: 'circuit', details: 'Chest to deck, full stand + hop' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Hanging Leg Raise', sets: 3, reps: 12, intensity: 'progressive', details: 'Controlled, no swing' },
        { name: 'Plank', sets: 3, reps: '45s', format: 'bodyweight', details: 'Braced, no sag' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress + flush the grip after carries' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const operatorStrength13WeekReal: WorkoutProgram = {
  metadata,
  periodization: OPERATOR_PERIODIZATION,
  templates: {
    // Testing weeks (W1, W13)
    'testing-week-day1': testingWeekDay1,
    'testing-week-day2': restDay,
    'testing-week-day3': testingWeekDay3,
    'testing-week-day4': restDay,
    'testing-week-day5': testingWeekDay5,
    'testing-week-day6': testingWeekDay6,
    'testing-week-day7': restDay,
    
    // Training cycle (W2-12)
    'training-day1': trainingDay1,
    'training-day2': trainingDay2,
    'training-day3': trainingDay3,
    'training-day4': trainingDay4,
    'training-day5': trainingDay5,
    'training-day6': trainingDay6,
    'training-day7': restDay,
  },
  workouts: [], // Legacy field, not used in template system
};

